import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { MultiSelectCheckbox } from '../../components/MultiSelectCheckbox';
import { ROUTES } from '../../consts/general';
import { META_INSIGHTS } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
    META_INSIGHTS_COLUMNS,
    fetchAllMetaReportData,
    renderMetaCell,
} from './metaReportHelpers';

const DEFAULT_END_DATE = format(new Date(), 'yyyy-MM-dd');
const DEFAULT_START_DATE = '2023-07-01';
const PAGE_SIZE = 50;

const MetaInsightsReport = ({ marginFromSidebar }) => {
    const navigate = useNavigate();
    const { beginRequest, isLatest } = useAbortableRequest();
    const initialReady = useInitialMountDelay(500);

    const [data, setData] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
    const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const columns = useMemo(() => META_INSIGHTS_COLUMNS, []);

    const accountOptions = useMemo(
        () => accounts.map((account) => ({
            value: account.accountId,
            label: account.label,
        })),
        [accounts]
    );

    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, selectedAccountIds, startDate, endDate]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const { data: response } = await axios.get('/api/v1/meta/accounts');
                if (cancelled) return;

                const nextAccounts = response?.accounts || [];
                setAccounts(nextAccounts);
                setSelectedAccountIds(nextAccounts.map((account) => account.accountId));
            } catch (fetchError) {
                console.error('Failed to load Meta accounts:', fetchError);
                if (!cancelled) {
                    setError('Could not load Meta accounts.');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const fetchData = useCallback(async () => {
        if (!selectedAccountIds.length) {
            setData([]);
            setTotalPages(1);
            setTotalRecords(0);
            return;
        }

        const { signal, requestId } = beginRequest();
        setIsLoading(true);
        setError(null);

        try {
            const { data: response } = await axios.get('/api/v1/meta/data', {
                signal,
                params: {
                    tab: 'insights',
                    page: currentPage,
                    limit: PAGE_SIZE,
                    search: debouncedSearch || undefined,
                    accountIds: selectedAccountIds.join(','),
                    startDate,
                    endDate,
                },
            });

            if (!isLatest(requestId)) return;

            setData(response?.items || []);
            setTotalPages(response?.totalPages || 1);
            setTotalRecords(response?.total || 0);
        } catch (fetchError) {
            if (fetchError?.name === 'AbortError') return;
            console.error(fetchError);
            if (!isLatest(requestId)) return;
            setError(fetchError?.response?.data?.error || fetchError.message || 'Failed to fetch report data');
            setData([]);
            setTotalPages(1);
            setTotalRecords(0);
        } finally {
            if (isLatest(requestId)) {
                setIsLoading(false);
            }
        }
    }, [
        beginRequest,
        isLatest,
        currentPage,
        debouncedSearch,
        selectedAccountIds,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        if (!initialReady || !selectedAccountIds.length) return;
        fetchData();
    }, [initialReady, fetchData, selectedAccountIds.length]);

    const renderRows = () =>
        (data || []).map((row, rowIndex) => (
            <tr key={`${row.id || row.ad_id}-${row.date}-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {columns.map((column) => (
                    <td
                        key={column.key}
                        className={[
                            'px-6 py-4 text-sm text-gray-900',
                            column.align === 'right' ? 'text-right whitespace-nowrap' : 'whitespace-nowrap',
                        ].join(' ')}
                    >
                        {renderMetaCell(column, row)}
                    </td>
                ))}
            </tr>
        ));

    const filtersComponent = (
        <div className="flex flex-row flex-wrap items-center gap-2 xl:items-end">
            <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search ad, campaign, account..."
                className="w-40 sm:w-[240px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <div className="w-40 sm:w-[280px]">
                <MultiSelectCheckbox
                    options={accountOptions}
                    value={selectedAccountIds}
                    onChange={setSelectedAccountIds}
                    placeholder="All accounts"
                    disabled={!accountOptions.length}
                />
            </div>
            <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
        </div>
    );

    const handleExportAll = useCallback(async () => {
        const items = await fetchAllMetaReportData({
            tab: 'insights',
            selectedAccountIds,
            search: debouncedSearch,
            totalRecords,
            startDate,
            endDate,
        });

        return { data: items, columns };
    }, [columns, selectedAccountIds, debouncedSearch, totalRecords, startDate, endDate]);

    const exportButton = (
        <CSVExportButton
            data={data}
            columns={columns}
            filename={META_INSIGHTS}
            disabled={isLoading || !totalRecords}
            onExport={handleExportAll}
        />
    );

    const paginationComponent =
        totalPages > 1 ? (
            <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
                    {totalRecords} results
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        type="button"
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        ) : null;

    return (
        <ReportTemplate
            title="Meta Insights"
            description="Daily Meta ad insights by account, campaign, ad set, and ad."
            breadcrumbs={[
                { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
                { label: 'Meta Insights' },
            ]}
            onBack={() => navigate(ROUTES.REPORTS)}
            marginFromSidebar={marginFromSidebar}
            isLoading={isLoading}
            error={error}
            filters={filtersComponent}
            exportButton={exportButton}
            data={data}
            columns={columns}
            pagination={paginationComponent}
            emptyMessage="No insights found for the selected filters."
            enableSorting={false}
        >
            {renderRows()}
        </ReportTemplate>
    );
};

export default MetaInsightsReport;
